/**
 * Spec 082 FR-F2 — candidate detail with edit-before-approve + reject flow.
 *
 * Minimal react-hook-form usage — editable fields with a direct POST on
 * submit. Approve → 201 → toast → navigate to /admin/rules. Reject uses a
 * small prompt for an optional reason.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { Icon } from '../../components/ui/Icon.js';
import { Button } from '../../components/ui/Button.js';
import {
  approveCandidate,
  getCandidate,
  rejectCandidate,
  type CandidateRule,
} from '../../api/ruleIngestion.js';
import { useToast } from '../../hooks/useToast.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { NearestRulesPanel } from '../../components/NearestRulesPanel.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { Select } from '../../components/Select.js';

interface FormShape {
  criterion: string;
  definition: string;
  evaluation_approach: string;
  epistemic_status: 'community_heuristic_single_source' | 'community_consensus' | 'research_backed';
  scoring_mode: 'g_eval' | 'dag';
  engines: string;
  content_types: string;
  slug: string;
}

export function RuleCandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [cand, setCand] = useState<CandidateRule | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, reset, control } = useForm<FormShape>({
    defaultValues: {
      criterion: '',
      definition: '',
      evaluation_approach: '',
      epistemic_status: 'community_heuristic_single_source',
      scoring_mode: 'g_eval',
      engines: '',
      content_types: '',
      slug: '',
    },
  });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getCandidate(id)
      .then((c) => {
        if (cancelled) return;
        setCand(c);
        reset({
          criterion: c.criterion,
          definition: c.definition,
          evaluation_approach: c.evaluation_approach,
          epistemic_status: 'community_heuristic_single_source',
          scoring_mode: 'g_eval',
          engines: c.engines_mentioned.join(', '),
          content_types: c.content_types_mentioned.join(', '),
          slug: '',
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reset]);

  async function onApprove(data: FormShape) {
    if (!id) return;
    setSubmitting(true);
    try {
      const res = await approveCandidate(id, {
        criterion: data.criterion,
        definition: data.definition,
        evaluation_approach: data.evaluation_approach,
        epistemic_status: data.epistemic_status,
        scoring_mode: data.scoring_mode,
        engines_mentioned: data.engines
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        content_types_mentioned: data.content_types
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        slug: data.slug.trim() || undefined,
      });
      toast.addToast(`Rule created: ${res.rule_slug}`, 'success');
      // Phase 9 Session E.3 (gap 084-R2-1) — redirect to the Rule Library
      // with `?slug=` so the new rule is highlighted + scrolled into view.
      // Falls back to `?id=` if the API somehow omits the slug. Reject still
      // routes back to the candidate queue (handled in onReject below).
      const target = res.rule_slug
        ? `/admin/rules?slug=${encodeURIComponent(res.rule_slug)}`
        : `/admin/rules?id=${encodeURIComponent(res.rule_id)}`;
      navigate(target);
    } catch (e) {
      toast.addToast(
        e instanceof Error ? e.message : 'Approve failed',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function onReject() {
    if (!id) return;
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Reason for rejection (optional):') ?? undefined;
    setSubmitting(true);
    try {
      await rejectCandidate(id, reason || undefined);
      toast.addToast('Candidate rejected', 'success');
      navigate('/admin/rules/candidates');
    } catch (e) {
      toast.addToast(
        e instanceof Error ? e.message : 'Reject failed',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="hub-page--reading p-6 text-slate-500" data-testid="candidate-detail-loading">
        Loading candidate…
      </div>
    );
  }
  if (err || !cand) {
    return (
      <div className="hub-page--reading p-6 rounded-md border border-rose-200 bg-rose-50 text-rose-800">
        {err ?? 'Not found'}
      </div>
    );
  }

  const isPending = cand.review_status === 'pending';

  // Phase 9 Session D — use the criterion as the current-page crumb,
  // truncated for long criteria. The Breadcrumb component applies its own
  // CSS truncation so this cap is only a hard ceiling.
  const crumbLabel =
    cand.criterion.length > 80 ? `${cand.criterion.slice(0, 80)}…` : cand.criterion;

  return (
    <div data-testid="admin-rule-candidate-detail" className="hub-page--reading space-y-6">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Rule candidates', to: '/admin/rules/candidates' },
          { label: crumbLabel },
        ]}
      />

      <header className="rounded-lg border border-slate-200 bg-white p-5">
        <h1 className="hub-page__title">{cand.criterion}</h1>
        <p className="mt-2 text-sm text-slate-600">
          From{' '}
          <a
            href={cand.source_post_url}
            target="_blank"
            rel="noreferrer"
            className="text-violet-600 hover:underline break-words"
          >
            {cand.source_post_title ?? cand.source_post_url}
          </a>
          {cand.source_post_author && <> by <strong>{cand.source_post_author}</strong></>}
          {' · '}
          confidence: <strong>{cand.author_confidence}</strong>
        </p>
      </header>

      <div className="rounded-lg border border-slate-200 bg-amber-50 p-4 text-sm">
        <div className="font-semibold text-amber-900">Supporting quote</div>
        <p className="mt-1 text-amber-900/90">“{cand.supporting_quote}”</p>
      </div>

      {/* Spec 101 — per-candidate similarity preview against the seeded rule library */}
      <NearestRulesPanel id={cand.id} />

      <form
        data-testid="candidate-approve-form"
        onSubmit={handleSubmit(onApprove)}
        className="hub-section space-y-4"
      >
        <div className="hub-section__head">
          <h2 className="text-lg font-semibold text-slate-900">Edit &amp; approve</h2>
        </div>

        <div className="hub-field">
          <label className="hub-field__label">Criterion (rule name)</label>
          <input
            type="text"
            {...register('criterion', { required: true })}
            disabled={!isPending}
            className="hub-input"
          />
        </div>

        <div className="hub-field">
          <label className="hub-field__label">Definition</label>
          <textarea
            rows={4}
            {...register('definition', { required: true })}
            disabled={!isPending}
            className="hub-input"
          />
        </div>

        <div className="hub-field">
          <label className="hub-field__label">Evaluation approach</label>
          <textarea
            rows={4}
            {...register('evaluation_approach', { required: true })}
            disabled={!isPending}
            className="hub-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="hub-field">
            <label className="hub-field__label">Epistemic status</label>
            <Controller
              control={control}
              name="epistemic_status"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  options={[
                    {
                      value: 'community_heuristic_single_source',
                      label: 'community_heuristic_single_source (default)',
                    },
                    { value: 'community_consensus', label: 'community_consensus' },
                    { value: 'research_backed', label: 'research_backed' },
                  ]}
                />
              )}
            />
          </div>
          <div className="hub-field">
            <label className="hub-field__label">Scoring mode</label>
            <Controller
              control={control}
              name="scoring_mode"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  options={[
                    { value: 'g_eval', label: 'g_eval' },
                    { value: 'dag', label: 'dag' },
                  ]}
                />
              )}
            />
          </div>
        </div>

        <div className="hub-field">
          <label className="hub-field__label">Slug (optional — auto-derived)</label>
          <input
            type="text"
            {...register('slug')}
            disabled={!isPending}
            placeholder="e.g. direct-answer-first-paragraph"
            className="hub-input font-mono"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="hub-field">
            <label className="hub-field__label">Engines (comma-separated)</label>
            <input
              type="text"
              {...register('engines')}
              disabled={!isPending}
              className="hub-input"
            />
          </div>
          <div className="hub-field">
            <label className="hub-field__label">Content types (comma-separated)</label>
            <input
              type="text"
              {...register('content_types')}
              disabled={!isPending}
              className="hub-input"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            variant="primary"
            type="submit"
            disabled={!isPending || submitting}
            data-testid="candidate-approve-submit"
          >
            <Icon name="bx-check" /> Approve
          </Button>
          <Button
            variant="ghost"
            disabled={!isPending || submitting}
            onClick={onReject}
            data-testid="candidate-reject-button"
          >
            <Icon name="bx-x" /> Reject
          </Button>
          {!isPending && (
            <span className="ml-auto self-center text-sm text-slate-500">
              Already reviewed: <strong>{cand.review_status}</strong>
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
